/**
 * @file components/settings/CropsPage.tsx
 * @description 可驗證作物字典管理頁。
 *
 * 功能：
 *   - 主要類別 Tab 切換（全部 / 農糧產品 / 畜禽產品 / 養殖水產品 / 林產品）
 *   - 品項名稱關鍵字搜尋
 *   - 啟用狀態過濾（Segmented）
 *   - 顯示 / 隱藏已軟刪記錄（Switch）
 *   - 分頁列表（含排序、啟用切換、編輯、刪除）
 *   - 新增 / 編輯 Drawer（CropFormDrawer）
 *
 * 法規依據：行政院農業委員會公告農企字第 1120012474 號。
 */
import { useMemo, useState } from 'react';
import { Button, Empty, Input, Segmented, Space, Switch, Table, Tabs, Tag, Typography } from 'antd';
import { message, modal } from '@/lib/antd-app';
import type { ColumnsType } from 'antd/es/table';
import { Download, Edit3, Plus, Trash2, UploadCloud } from 'lucide-react';
import type { CropDto, CropMainCategory, CropScheme } from '@zhcb/shared';
import { CROP_MAIN_CATEGORIES_TAP, CROP_MAIN_CATEGORIES_TOP } from '@zhcb/shared';
import { buildCropsExportUrl, useCropsList, useDeleteCrop, useUpdateCrop } from '@/hooks/useCrops';
import { CropFormDrawer } from '@/components/settings/CropFormDrawer';
import { CropImportModal } from '@/components/settings/CropImportModal';

/** 啟用狀態過濾器類型 */
type ActiveFilter = 'all' | 'active' | 'inactive';

/** 各方案的主要類別 Tab 選項（序157：TOP/TAP 分類體系不同） */
const MAIN_CATEGORY_TABS: Record<CropScheme, readonly string[]> = {
  TOP: CROP_MAIN_CATEGORIES_TOP,
  TAP: CROP_MAIN_CATEGORIES_TAP,
};

export function CropsPage() {
  // ── 篩選狀態 ──────────────────────────────────────────────────
  /** 驗證方案（TOP/TAP，序230） */
  const [scheme, setScheme] = useState<CropScheme>('TAP');
  /** 主要類別 Tab 選中值；undefined 代表「全部」 */
  const [mainCategory, setMainCategory] = useState<CropMainCategory | undefined>(undefined);
  /** 搜尋關鍵字（品項名稱） */
  const [search, setSearch] = useState('');
  /** 啟用狀態過濾 */
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('all');
  /** 是否顯示已軟刪記錄 */
  const [includeDeleted, setIncludeDeleted] = useState(false);

  // ── 分頁狀態 ──────────────────────────────────────────────────
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // ── Drawer 狀態 ───────────────────────────────────────────────
  const [drawerOpen, setDrawerOpen] = useState(false);
  /** 當前編輯的作物記錄；null 代表新增模式 */
  const [editing, setEditing] = useState<CropDto | null>(null);
  /** 上傳並識別匯入彈窗狀態（序157） */
  const [importOpen, setImportOpen] = useState(false);

  // ── 資料查詢 ──────────────────────────────────────────────────
  const listQ = useCropsList({
    page,
    pageSize,
    search: search.trim() || undefined,
    scheme,
    mainCategory,
    activeOnly: activeFilter === 'active',
    includeDeleted,
  });
  const update = useUpdateCrop();
  const remove = useDeleteCrop();

  /**
   * 前端再做 inactive 過濾（API 只支援 activeOnly=true，
   * 「停用」需在 client 端過濾）。
   */
  const items = useMemo(() => {
    const all = listQ.data?.items ?? [];
    if (activeFilter === 'inactive') return all.filter((x) => !x.isActive);
    return all;
  }, [listQ.data?.items, activeFilter]);

  // ── 方案 / Tab 切換處理 ──────────────────────────────────────────
  /** 切換 TOP/TAP 方案時，該方案的主要類別枚舉不同，一併清除主要類別過濾並重置頁碼 */
  const onSchemeChange = (next: CropScheme) => {
    setScheme(next);
    setMainCategory(undefined);
    setPage(1);
  };

  /**
   * 切換主要類別 Tab 時同步清除次要類別過濾並重置頁碼。
   */
  const onTabChange = (key: string) => {
    setMainCategory(key === 'all' ? undefined : (key as CropMainCategory));
    setPage(1);
  };

  // ── Drawer 操作 ───────────────────────────────────────────────
  const openCreate = () => {
    setEditing(null);
    setDrawerOpen(true);
  };

  const openEdit = (row: CropDto) => {
    setEditing(row);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditing(null);
  };

  // ── 啟用狀態切換 ──────────────────────────────────────────────
  /**
   * 行內切換作物啟用狀態，呼叫 PATCH API 局部更新。
   */
  const toggleActive = async (row: CropDto, next: boolean) => {
    try {
      await update.mutateAsync({ id: row.id, input: { isActive: next } });
      message.success(next ? '已啟用' : '已停用');
    } catch (e) {
      message.error((e as Error).message || '操作失敗');
    }
  };

  // ── 軟刪除確認 ────────────────────────────────────────────────
  /**
   * 點擊刪除時彈出確認框，確認後執行軟刪除（記錄保留，deletedAt 填入時間）。
   */
  const onDelete = (row: CropDto) => {
    modal.confirm({
      title: `軟刪除「${row.name}」？`,
      content: '軟刪除後不再出現在管理列表（除非開「顯示已刪除」）與下拉選項。',
      okText: '刪除',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await remove.mutateAsync(row.id);
          message.success('已刪除');
        } catch (e) {
          message.error((e as Error).message || '刪除失敗');
        }
      },
    });
  };

  // ── 表格欄位定義 ──────────────────────────────────────────────
  const columns: ColumnsType<CropDto> = [
    {
      title: '主要類別',
      dataIndex: 'mainCategory',
      width: 110,
    },
    {
      title: '次要類別',
      dataIndex: 'subCategory',
      width: 110,
    },
    {
      title: '品項組',
      dataIndex: 'itemGroup',
      width: 120,
      /** 蔬菜類以外無品項組，顯示長破折號 */
      render: (v: string | null) => v ?? '—',
    },
    {
      title: '品項名稱',
      dataIndex: 'name',
      width: 160,
    },
    {
      title: '產品範圍',
      dataIndex: 'productScope',
      width: 160,
      /** 僅 TOP 使用，TAP 一律顯示長破折號 */
      render: (v: string | null) => v ?? '—',
    },
    {
      title: '備註',
      dataIndex: 'remark',
      width: 200,
      ellipsis: true,
      render: (v: string | null) => v ?? '—',
    },
    {
      title: '排序',
      dataIndex: 'sortOrder',
      width: 70,
    },
    {
      title: '啟用',
      dataIndex: 'isActive',
      width: 80,
      render: (v: boolean, row) => (
        <Switch
          size="small"
          checked={v}
          onChange={(next) => void toggleActive(row, next)}
          disabled={!!row.deletedAt}
        />
      ),
    },
    {
      title: '狀態',
      width: 90,
      render: (_, row) =>
        row.deletedAt ? <Tag color="default">已刪除</Tag> : <Tag color="green">正常</Tag>,
    },
    {
      title: '操作',
      width: 120,
      render: (_, row) =>
        row.deletedAt ? (
          <Typography.Text type="secondary">已刪除</Typography.Text>
        ) : (
          <Space size="small">
            <Button
              type="link"
              size="small"
              icon={<Edit3 size={12} />}
              onClick={() => openEdit(row)}
            >
              編輯
            </Button>
            <Button
              type="link"
              size="small"
              danger
              icon={<Trash2 size={12} />}
              onClick={() => onDelete(row)}
            >
              刪除
            </Button>
          </Space>
        ),
    },
  ];

  // ── 渲染 ──────────────────────────────────────────────────────
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {/* 頁面標題與說明 */}
      <Typography.Title level={4} style={{ margin: 0 }}>
        作物管理
      </Typography.Title>
      <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
        維護可驗證作物品項字典（依行政院農業委員會公告農企字第 1120012474 號）。
      </Typography.Paragraph>

      {/* 驗證方案切換（序230：TOP/TAP 分類體系不同，需先分流） */}
      <Segmented
        value={scheme}
        onChange={(v) => onSchemeChange(v as CropScheme)}
        options={[
          { label: '有機 TOP', value: 'TOP' },
          { label: '產銷履歷 TAP', value: 'TAP' },
        ]}
      />

      {/* 主要類別 Tab 切換（依方案切換選項） */}
      <Tabs
        activeKey={mainCategory ?? 'all'}
        onChange={onTabChange}
        items={[
          { key: 'all', label: '全部' },
          ...MAIN_CATEGORY_TABS[scheme].map((c) => ({ key: c, label: c })),
        ]}
        style={{ marginBottom: 0 }}
      />

      {/* 工具列：搜尋 / 狀態篩選 / 顯示已刪除 / 匯出 / 新增按鈕 */}
      <Space wrap>
        <Input.Search
          placeholder="搜尋品項名稱"
          allowClear
          style={{ width: 260 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onSearch={() => setPage(1)}
        />
        <Segmented
          value={activeFilter}
          onChange={(v) => {
            setActiveFilter(v as ActiveFilter);
            setPage(1);
          }}
          options={[
            { label: '全部', value: 'all' },
            { label: '啟用', value: 'active' },
            { label: '停用', value: 'inactive' },
          ]}
        />
        <Space>
          <Switch checked={includeDeleted} onChange={setIncludeDeleted} />
          <Typography.Text>顯示已刪除</Typography.Text>
        </Space>
        <Button
          icon={<Download size={14} />}
          onClick={() => {
            window.location.href = buildCropsExportUrl({
              scheme,
              mainCategory,
              search: search.trim() || undefined,
              activeOnly: activeFilter === 'active',
            });
          }}
        >
          匯出
        </Button>
        <Button icon={<UploadCloud size={14} />} onClick={() => setImportOpen(true)}>
          上傳並識別
        </Button>
        <Button type="primary" icon={<Plus size={14} />} onClick={openCreate}>
          新增作物
        </Button>
      </Space>

      {/* 資料表格 */}
      {listQ.isLoading ? (
        <Typography.Text>載入中…</Typography.Text>
      ) : items.length ? (
        <Table<CropDto>
          rowKey="id"
          dataSource={items}
          columns={columns}
          size="small"
          pagination={{
            current: page,
            pageSize,
            /**
             * inactive 過濾為客戶端過濾（API 無 inactiveOnly 參數），
             * 分頁 total 用 items.length 避免顯示錯誤的總筆數。
             */
            total: activeFilter === 'inactive' ? items.length : (listQ.data?.total ?? 0),
            showSizeChanger: true,
            onChange: (p, s) => {
              setPage(p);
              setPageSize(s);
            },
          }}
        />
      ) : (
        <Empty description="尚無資料" />
      )}

      {/* 新增 / 編輯 Drawer */}
      <CropFormDrawer
        open={drawerOpen}
        editing={editing}
        defaultScheme={scheme}
        onClose={closeDrawer}
      />

      {/* 上傳並識別匯入彈窗（序157） */}
      <CropImportModal
        open={importOpen}
        defaultScheme={scheme}
        onClose={() => setImportOpen(false)}
        onImported={() => void listQ.refetch()}
      />
    </Space>
  );
}
